import React from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface RecipientBadgeProps {
  status: 'sent' | 'viewed' | 'none';
  type: 'borrower' | 'co_borrower' | 'partner';
  recipientName?: string;
  recipientEmail?: string;
  viewCount?: number;
  lastViewedAt?: string | null;
  onClick?: () => void;
}

const RECIPIENT_TYPE_LABELS = {
  borrower: 'Borrower',
  co_borrower: 'Co-Borrower',
  partner: 'Partner'
} as const;

export const RecipientBadge: React.FC<RecipientBadgeProps> = ({
  status,
  type,
  recipientName,
  recipientEmail,
  viewCount = 0,
  lastViewedAt,
  onClick
}) => {
  // Don't render if status is 'none'
  if (status === 'none') {
    return (
      <div className="inline-flex items-center justify-center h-6 w-8 rounded border border-dashed border-gray-300 bg-gray-50 text-gray-400 text-xs font-medium">
        -
      </div>
    );
  }

  const isViewed = status === 'viewed';

  const badgeClasses = cn(
    "inline-flex items-center justify-center h-6 w-8 rounded border text-xs font-semibold transition-colors cursor-pointer",
    isViewed
      ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
      : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
  );

  const tooltipContent = (
    <div className="space-y-1">
      <div className="font-semibold">{RECIPIENT_TYPE_LABELS[type]}</div>
      {recipientName && <div className="text-sm">{recipientName}</div>}
      {recipientEmail && <div className="text-xs text-white/80">{recipientEmail}</div>}
      <div className="text-xs text-white/70 pt-1 border-t border-white/20">
        {isViewed ? (
          <>
            <div>Viewed {viewCount} {viewCount === 1 ? 'time' : 'times'}</div>
            {lastViewedAt && (
              <div>Last viewed: {new Date(lastViewedAt).toLocaleString()}</div>
            )}
          </>
        ) : (
          <div>Sent, not yet viewed</div>
        )}
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className={badgeClasses}
            type="button"
          >
            {isViewed ? 'V' : 'S'}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs bg-slate-800 text-white border-slate-700">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
